import type { ClientHandover, ClientWorkstream, ClientWorkstreamPipeline } from '@/types/client-workstreams';
import { z } from 'zod';
import { safeParseDate } from '@/lib/dateHelpers';
import { normalizeRenewalTimeZone } from '../../supabase/functions/_shared/client-renewal';

const calendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
  const date = safeParseDate(`${value}T00:00:00Z`);
  return Boolean(date && date.toISOString().slice(0, 10) === value);
}, 'Choose a valid calendar date');
export const clientHandoverSchema = z.object({ services: z.boolean(), software: z.boolean(), starts_on: calendarDate.nullable(), ends_on: calendarDate.nullable() })
  .refine(value => value.services || value.software, 'Choose at least one part of the sale')
  .refine(value => !value.software || Boolean(value.starts_on && value.ends_on && value.ends_on > value.starts_on), 'Record the licence start and renewal dates');
export const workstreamPatchSchema = z.object({
  action: z.enum(['update', 'renew']).optional(), stage_id: z.string().uuid().optional(),
  starts_on: calendarDate.nullable().optional(), ends_on: calendarDate.nullable().optional(),
  status: z.enum(['active', 'completed', 'churned']).optional(), next_action: z.string().nullable().optional(),
  next_action_on: calendarDate.nullable().optional(), reason: z.string().optional(),
  tasks: z.array(z.object({ id: z.string().uuid(), title: z.string().trim().min(1), due_on: calendarDate.nullable(), completed: z.boolean() })).optional(),
}).strict();

export function effectiveWorkstreamStage(stream: ClientWorkstream, config: ClientWorkstreamPipeline, today: string): string {
  if (stream.kind !== 'software' || stream.status !== 'active' || !stream.ends_on || !config.renewal_stage_id) return stream.stage_id;
  // Calendar dates, in the user's timezone. No background writer or false
  // renewal: the card remains due until a confirmed new term is recorded.
  const days = Math.round((Date.parse(`${stream.ends_on}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000);
  return days <= 60 ? config.renewal_stage_id : stream.stage_id;
}

export function validClientHandover(value: ClientHandover): boolean {
  return clientHandoverSchema.safeParse(value).success;
}

export function orderPipelineMenu<T extends { id: string }>(items: readonly T[], order: readonly string[]): T[] {
  const positions = new Map(order.map((id, index) => [id, index]));
  return [...items].sort((a, b) => (positions.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (positions.get(b.id) ?? Number.MAX_SAFE_INTEGER));
}

export function calendarToday(timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: normalizeRenewalTimeZone(timeZone), year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const value = (key: string) => parts.find(part => part.type === key)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

export interface MenuPipeline { id: string; name: string; methodology_slug?: string | null; }
export interface MenuMethodology { id: string; name: string; slug?: string; }
export function pipelineMenuEntries(pipelines: MenuPipeline[], methodologies: MenuMethodology[], order: string[]) {
  const migrated = new Set(pipelines.map(pipeline => pipeline.methodology_slug).filter(Boolean));
  return orderPipelineMenu([
    ...pipelines.map(pipeline => ({ id: pipeline.id, name: pipeline.name })),
    ...methodologies.filter(method => !method.slug || !migrated.has(method.slug)).map(method => ({ id: `methodology:${method.id}`, name: method.name })),
  ], order);
}
